import React, { useState, useEffect } from "react";
import {
  Box, Card, Container, Typography, Grid, CircularProgress, Chip, Stack,
  ToggleButton, ToggleButtonGroup, LinearProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";

const GREEN = "#3dc10e";
const ORANGE = "#f28c3e";
const RED = "#FF4842";
const BLUE = "#4FC3F7";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{ bgcolor: "#222", border: "1px solid #333", borderRadius: 2, p: 1.5 }}>
        <Typography variant="caption" sx={{ color: "#aaa", display: "block", mb: 0.5 }}>{label}</Typography>
        {payload.map((p) => (
          <Typography key={p.name} variant="caption" sx={{ color: p.color, display: "block" }}>
            {p.name}: {p.value}{typeof p.value === "number" && p.name.includes("%") ? "%" : ""}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

export default function BrokerageHealth() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "User"), orderBy("createdAt", "desc")));
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (_) {}
      setLoading(false);
    };
    loadUsers();
  }, []);

  // Compute stats
  const total = users.length;
  const connected = users.filter((u) => u.brokerageConnected).length;
  const connectionRate = total > 0 ? ((connected / total) * 100).toFixed(1) : 0;

  // Brokerage type breakdown
  const brokerageTypes = users.reduce((acc, u) => {
    if (!u.brokerageConnected) return acc;
    const type = u.brokerageType || "Unknown";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const brokeragePieData = Object.entries(brokerageTypes).map(([name, value]) => ({ name, value }));
  const PIE_COLORS = [GREEN, ORANGE, BLUE, "#CE93D8", "#FFE700", RED];

  // Daily connection rate over time
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  const dailyData = (() => {
    const buckets = {};
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      buckets[key] = { total: 0, connected: 0 };
    }
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    users.forEach((u) => {
      if (!u.createdAt) return;
      const d = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
      if (d < cutoff) return;
      const key = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      if (!(key in buckets)) return;
      buckets[key].total++;
      if (u.brokerageConnected) buckets[key].connected++;
    });

    return Object.entries(buckets).map(([date, { total, connected }]) => ({
      date,
      "New Users": total,
      "Connected": connected,
      "Connection %": total > 0 ? parseFloat(((connected / total) * 100).toFixed(1)) : 0,
    }));
  })();

  // Signup method breakdown
  const signupMethods = users.reduce((acc, u) => {
    const method = u.signUpMethod || u.provider || "email";
    acc[method] = (acc[method] || 0) + 1;
    return acc;
  }, {});

  const signupData = Object.entries(signupMethods).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
  }));

  return (
    <Page title="Brokerage Health | Fire Investing Admin">
      <Container maxWidth="xl">
        <Box sx={{ pb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Brokerage & Onboarding Health</Typography>
            <Typography variant="body2" color="text.secondary">
              Snap API connection rates, user sign-up methods, and onboarding funnel
            </Typography>
          </Box>
          <ToggleButtonGroup size="small" value={range} exclusive onChange={(_, v) => v && setRange(v)}>
            <ToggleButton value="7d">7D</ToggleButton>
            <ToggleButton value="30d">30D</ToggleButton>
            <ToggleButton value="90d">90D</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress sx={{ color: GREEN }} />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={6} md={3}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>Total Users</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: BLUE }}>{total}</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>Brokerage Connected</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: GREEN }}>{connected}</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>Connection Rate</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: connectionRate >= 50 ? GREEN : ORANGE }}>
                    {connectionRate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate" value={parseFloat(connectionRate)}
                    sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.1)",
                      "& .MuiLinearProgress-bar": { bgcolor: connectionRate >= 50 ? GREEN : ORANGE } }}
                  />
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>Not Connected</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: RED }}>{total - connected}</Typography>
                  <Typography variant="caption" color="text.secondary">onboarding incomplete</Typography>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* Connection trend */}
              <Grid item xs={12}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>New Signups vs. Brokerage Connections Over Time</Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={dailyData}>
                      <defs>
                        <linearGradient id="usersGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={BLUE} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={BLUE} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="connGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={GREEN} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#666", fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="New Users" stroke={BLUE} fill="url(#usersGrad)" strokeWidth={2} />
                      <Area type="monotone" dataKey="Connected" stroke={GREEN} fill="url(#connGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              </Grid>

              {/* Brokerage type pie */}
              <Grid item xs={12} md={5}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", height: "100%" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Brokerage Type Breakdown</Typography>
                  {brokeragePieData.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                      No brokerage data yet
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={brokeragePieData} cx="50%" cy="50%" outerRadius={80}
                          dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}>
                          {brokeragePieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Grid>

              {/* Signup methods */}
              <Grid item xs={12} md={7}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Sign-up Methods</Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={signupData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" tick={{ fill: "#666", fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "#aaa", fontSize: 12 }} width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={ORANGE} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Page>
  );
}
